/**
 * Query Key Factory
 *
 * Centralized query key management for React Query.
 *
 * Pattern: [resource, scope?, filters?]
 *
 * Benefits:
 * - Type-safe query keys
 * - Consistent structure across the app
 * - Easy invalidation
 * - Autocomplete support
 *
 * QUERY KEY PATTERNS:
 *
 * 1. **all()** - Root namespace for a resource
 *    Use for: Invalidating ALL queries for a resource
 *    @example queryClient.invalidateQueries(queryKeys.orders.all())
 *
 * 2. **lists()** - Base key for all list queries
 *    Use for: Invalidating all list variations (filtered and unfiltered)
 *    @example queryClient.invalidateQueries(queryKeys.orders.lists())
 *
 * 3. **list(filters?)** - Specific list query with optional filters
 *    Use for: Fetching lists with or without filters
 *    @example
 *    // Unfiltered list
 *    queryKeys.orders.list()
 *    // Filtered list
 *    queryKeys.orders.list({ status: 'pending', user: 'me' })
 *
 * 4. **details()** - Base key for all detail queries
 *    Use for: Invalidating all detail queries for a resource
 *    @example queryClient.invalidateQueries(queryKeys.orders.details())
 *
 * 5. **detail(id)** - Specific detail query for one entity
 *    Use for: Fetching single entity details
 *    @example queryKeys.orders.detail(orderId)
 *
 * 6. **Scoped queries** (byProject, byOrg, etc.)
 *    Use for: Queries filtered by a parent relationship
 *    @example
 *    queryKeys.sites.byProject(projectId)
 *    queryKeys.permissions.byUser(userId)
 *
 * INVALIDATION STRATEGIES:
 *
 * - Invalidate all: `queryKeys.orders.all()`
 * - Invalidate all lists: `queryKeys.orders.lists()`
 * - Invalidate all details: `queryKeys.orders.details()`
 * - Invalidate specific query: `queryKeys.orders.detail(id)`
 * - Invalidate scoped queries: `queryKeys.sites.byProject(projectId)`
 *
 * COMMON PATTERNS:
 *
 * After creating/updating an entity:
 * ```ts
 * // Invalidate the specific detail and all lists
 * queryClient.invalidateQueries(queryKeys.orders.detail(orderId))
 * queryClient.invalidateQueries(queryKeys.orders.lists())
 * ```
 *
 * After deleting an entity:
 * ```ts
 * // Remove the specific detail and invalidate all lists
 * queryClient.removeQueries(queryKeys.orders.detail(orderId))
 * queryClient.invalidateQueries(queryKeys.orders.lists())
 * ```
 *
 * When entity relationships change:
 * ```ts
 * // If a site's project changes, invalidate project-scoped queries
 * queryClient.invalidateQueries(queryKeys.sites.byProject(oldProjectId))
 * queryClient.invalidateQueries(queryKeys.sites.byProject(newProjectId))
 * ```
 */
import type {
  OrderId,
  ProjectId,
  LoggerId,
  SiteId,
  CableId,
  ChallengeId,
  OrganizationId,
  UserId,
  BlueprintPointId,
  ModemId,
  NoteId,
  NotificationId,
  LoggerActivationId,
  ProductServiceId,
  ReportConfigurationId,
  ReportInstanceId,
  SensorId,
  ServiceContractId,
  ServiceItemId,
  OrderLineItemId,
} from '@beaded/models'

export const queryKeys = {
  // Orders
  orders: {
    all: () => ['orders'] as const,
    lists: () => [...queryKeys.orders.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.orders.lists(), filters] as const)
        : queryKeys.orders.lists(),
    details: () => [...queryKeys.orders.all(), 'detail'] as const,
    detail: (id: OrderId) => [...queryKeys.orders.details(), { id }] as const,
    resyncPlan: (id: OrderId) =>
      [...queryKeys.orders.detail(id), 'resync-plan'] as const,
  },

  // Order Line Items
  orderLineItems: {
    all: () => ['orderLineItems'] as const,
    details: () => [...queryKeys.orderLineItems.all(), 'detail'] as const,
    detail: (id: OrderLineItemId) =>
      [...queryKeys.orderLineItems.details(), { id }] as const,
  },

  // changeLogs
  changeLogs: {
    all: () => ['changeLogs'] as const,
    lists: () => [...queryKeys.changeLogs.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.changeLogs.lists(), filters] as const)
        : queryKeys.changeLogs.lists(),
    forProject: (projectId: ProjectId) =>
      [...queryKeys.changeLogs.all(), 'for-project', { projectId }] as const,
  },

  // Projects
  projects: {
    all: () => ['projects'] as const,
    lists: () => [...queryKeys.projects.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.projects.lists(), filters] as const)
        : queryKeys.projects.lists(),
    details: () => [...queryKeys.projects.all(), 'detail'] as const,
    detail: (id: ProjectId) =>
      [...queryKeys.projects.details(), { id }] as const,
    byOrg: (orgId: OrganizationId) =>
      [...queryKeys.projects.all(), 'by-org', { orgId }] as const,
  },

  // Loggers
  loggers: {
    all: () => ['loggers'] as const,
    lists: () => [...queryKeys.loggers.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.loggers.lists(), filters] as const)
        : queryKeys.loggers.lists(),
    details: () => [...queryKeys.loggers.all(), 'detail'] as const,
    detail: (id: LoggerId) => [...queryKeys.loggers.details(), { id }] as const,
    /** The fleet-wide last transmissions (Logger Status page). */
    lastTransmissions: () =>
      [...queryKeys.loggers.all(), 'last-transmissions'] as const,
    manufacturers: () => [...queryKeys.loggers.all(), 'manufacturers'] as const,
    apiKey: (id: LoggerId) =>
      [...queryKeys.loggers.detail(id), 'apiKey'] as const,
    /** The first page of a logger's events, for the given types. */
    events: (id: LoggerId, types: string) =>
      [...queryKeys.loggers.detail(id), 'events', { types }] as const,
    /** The newest Iridium messages of a logger (global view only). */
    messages: (id: LoggerId) =>
      [...queryKeys.loggers.detail(id), 'messages'] as const,
  },

  // Vendor device import (remote vendor devices diffed against our loggers)
  vendorDevices: {
    all: () => ['vendorDevices'] as const,
    list: (vendor: string) =>
      [...queryKeys.vendorDevices.all(), 'list', { vendor }] as const,
  },

  // Logger Activations
  loggerActivations: {
    all: () => ['loggerActivations'] as const,
    lists: () => [...queryKeys.loggerActivations.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.loggerActivations.lists(), filters] as const)
        : queryKeys.loggerActivations.lists(),
    details: () => [...queryKeys.loggerActivations.all(), 'detail'] as const,
    forLogger: (id: LoggerId) =>
      [...queryKeys.loggerActivations.details(), { id }] as const,
    detail: (id: LoggerActivationId) =>
      [...queryKeys.loggerActivations.details(), { id }] as const,
  },

  // Sites
  sites: {
    all: () => ['sites'] as const,
    lists: () => [...queryKeys.sites.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.sites.lists(), filters] as const)
        : queryKeys.sites.lists(),
    byProject: (projectId: ProjectId) =>
      [...queryKeys.sites.all(), 'by-project', { projectId }] as const,
    details: () => [...queryKeys.sites.all(), 'detail'] as const,
    detail: (id: SiteId) => [...queryKeys.sites.details(), { id }] as const,
    changelog: (id: SiteId) =>
      [...queryKeys.sites.detail(id), 'changelog'] as const,
  },

  // Cables
  cables: {
    all: () => ['cables'] as const,
    lists: () => [...queryKeys.cables.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.cables.lists(), filters] as const)
        : queryKeys.cables.lists(),
    details: () => [...queryKeys.cables.all(), 'detail'] as const,
    detail: (id: CableId | string) =>
      [...queryKeys.cables.details(), { id }] as const,
    prices: (type?: string) =>
      type
        ? ([...queryKeys.cables.all(), 'prices', { type }] as const)
        : ([...queryKeys.cables.all(), 'prices'] as const),
  },

  // Sensors
  sensors: {
    all: () => ['sensors'] as const,
    lists: () => [...queryKeys.sensors.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.sensors.lists(), filters] as const)
        : queryKeys.sensors.lists(),
    byCable: (serial: string) =>
      [...queryKeys.sensors.all(), 'by-cable', { serial }] as const,
    byId: (id: string | number) =>
      [...queryKeys.sensors.all(), 'by-id', { id }] as const,
  },

  // Organizations
  orgs: {
    all: () => ['orgs'] as const,
    lists: () => [...queryKeys.orgs.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.orgs.lists(), filters] as const)
        : queryKeys.orgs.lists(),
    details: () => [...queryKeys.orgs.all(), 'detail'] as const,
    detail: (id: OrganizationId) =>
      [...queryKeys.orgs.details(), { id }] as const,
  },

  // Users
  users: {
    all: () => ['users'] as const,
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.users.lists(), filters] as const)
        : queryKeys.users.lists(),
    details: () => [...queryKeys.users.all(), 'detail'] as const,
    detail: (id: UserId) => [...queryKeys.users.details(), { id }] as const,
    recent: () => [...queryKeys.users.all(), 'recent'] as const,
  },

  // API Keys (session user's own keys -- #3687)
  apiKeys: {
    all: () => ['apiKeys'] as const,
    lists: () => [...queryKeys.apiKeys.all(), 'list'] as const,
    mine: () => [...queryKeys.apiKeys.lists(), 'mine'] as const,
  },

  // Modems
  modems: {
    all: () => ['modems'] as const,
    lists: () => [...queryKeys.modems.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.modems.lists(), filters] as const)
        : queryKeys.modems.lists(),
    details: () => [...queryKeys.modems.all(), 'detail'] as const,
    detail: (id: ModemId | string) =>
      [...queryKeys.modems.details(), { id }] as const,
  },

  // Permissions
  permissions: {
    all: () => ['permissions'] as const,
    byUser: (userId: UserId) =>
      [...queryKeys.permissions.all(), 'by-user', { userId }] as const,
    byOrg: (orgId: OrganizationId) =>
      [...queryKeys.permissions.all(), 'by-org', { orgId }] as const,
    byProject: (projectId: ProjectId) =>
      [...queryKeys.permissions.all(), 'by-project', { projectId }] as const,
  },

  // Readings
  readings: {
    all: () => ['readings'] as const,
    byProject: (projectId: ProjectId, filters?: Record<string, unknown>) =>
      filters
        ? ([
            ...queryKeys.readings.all(),
            'by-project',
            { projectId },
            filters,
          ] as const)
        : ([...queryKeys.readings.all(), 'by-project', { projectId }] as const),
    bySiteTrace: (siteId: SiteId | number, traceId: string) =>
      [
        ...queryKeys.readings.all(),
        'by-site',
        { siteId },
        { traceId },
      ] as const,
    siteLines: (siteId: SiteId | number) =>
      [...queryKeys.readings.all(), 'lines', 'site', { siteId }] as const,
    projectLines: (projectId: ProjectId) =>
      [...queryKeys.readings.all(), 'lines', 'project', { projectId }] as const,
    loggerLines: (loggerSerial: string) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'logger',
        { loggerSerial },
      ] as const,
    blueprintLines: (projectId: ProjectId) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'blueprint',
        { projectId },
      ] as const,
    santosForecast: (siteId: number) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'santos-forecast',
        { siteId },
      ] as const,
    santosForecastCache: (siteId: number) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'santos-forecast-cache',
        { siteId },
      ] as const,
    tundraForecast: (siteId: number) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'tundra-forecast',
        { siteId },
      ] as const,
    tundraForecastCache: (siteId: number) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'tundra-forecast-cache',
        { siteId },
      ] as const,
    fleetTemperatureForecastCache: (siteId: number) =>
      [
        ...queryKeys.readings.all(),
        'lines',
        'fleet-temperature-forecast-cache',
        { siteId },
      ] as const,
  },

  // Blueprint
  blueprint: {
    all: () => ['blueprint'] as const,
    points: (projectId: ProjectId) =>
      [...queryKeys.blueprint.all(), 'points', { projectId }] as const,
    point: (projectId: ProjectId, pointId: BlueprintPointId) =>
      [...queryKeys.blueprint.all(), 'point', { projectId, pointId }] as const,
    data: (projectId: ProjectId) =>
      [...queryKeys.blueprint.all(), 'data', { projectId }] as const,
  },

  // Notifications
  notifications: {
    all: () => ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.notifications.lists(), filters] as const)
        : queryKeys.notifications.lists(),
    details: () => [...queryKeys.notifications.all(), 'detail'] as const,
    detail: (id: NotificationId) =>
      [...queryKeys.notifications.details(), { id }] as const,
  },

  // Notes
  notes: {
    all: () => ['notes'] as const,
    lists: () => [...queryKeys.notes.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.notes.lists(), filters] as const)
        : queryKeys.notes.lists(),
    byNotification: (notificationId: NotificationId) =>
      [
        ...queryKeys.notes.all(),
        'by-notification',
        { notificationId },
      ] as const,
  },

  // Service Items
  serviceItems: {
    all: () => ['service-items'] as const,
    lists: () => [...queryKeys.serviceItems.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.serviceItems.lists(), filters] as const)
        : queryKeys.serviceItems.lists(),
    details: () => [...queryKeys.serviceItems.all(), 'detail'] as const,
    detail: (id: ServiceItemId) =>
      [...queryKeys.serviceItems.details(), { id }] as const,
    chain: (id: ServiceItemId) =>
      [...queryKeys.serviceItems.all(), 'chain', { id }] as const,
    unlinked: (orgId: OrganizationId) =>
      [...queryKeys.serviceItems.all(), 'unlinked', { orgId }] as const,
  },

  // Public Pages (different namespace)
  public: {
    all: () => ['public'] as const,
    org: (orgId: OrganizationId) =>
      [...queryKeys.public.all(), 'org', { orgId }] as const,
    orgs: () => [...queryKeys.public.all(), 'orgs', 'list'] as const,
    project: (projectId: ProjectId) =>
      [...queryKeys.public.all(), 'project', { projectId }] as const,
    projects: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.public.all(), 'projects', 'list', filters] as const)
        : ([...queryKeys.public.all(), 'projects', 'list'] as const),
    site: (siteId: SiteId | string) =>
      [
        ...queryKeys.public.all(),
        'site',
        typeof siteId === 'string' ? { guid: siteId } : { siteId },
      ] as const,
    sites: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.public.all(), 'sites', 'list', filters] as const)
        : ([...queryKeys.public.all(), 'sites', 'list'] as const),
    chartData: (siteId: SiteId | string, filters?: Record<string, unknown>) =>
      filters
        ? ([
            ...queryKeys.public.all(),
            'chart-data',
            typeof siteId === 'string' ? { guid: siteId } : { siteId },
            filters,
          ] as const)
        : ([
            ...queryKeys.public.all(),
            'chart-data',
            typeof siteId === 'string' ? { guid: siteId } : { siteId },
          ] as const),
  },

  // Reports
  reports: {
    all: () => ['reports'] as const,
    project: (projectId: ProjectId) =>
      [...queryKeys.reports.all(), 'project', { projectId }] as const,
    wiv: (projectId: ProjectId) =>
      [...queryKeys.reports.all(), 'wiv', { projectId }] as const,
    wivWeeks: (projectId: ProjectId) =>
      [...queryKeys.reports.all(), 'wiv-weeks', { projectId }] as const,
  },

  // Report Templates
  reportTemplates: {
    all: () => ['report-templates'] as const,
    lists: () => [...queryKeys.reportTemplates.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.reportTemplates.lists(), filters] as const)
        : queryKeys.reportTemplates.lists(),
    details: () => [...queryKeys.reportTemplates.all(), 'detail'] as const,
    detail: (id: number) =>
      [...queryKeys.reportTemplates.details(), { id }] as const,
    byType: (templateType: string) =>
      [
        ...queryKeys.reportTemplates.all(),
        'by-type',
        { templateType },
      ] as const,
  },

  // Report Configurations
  reportConfigurations: {
    all: () => ['report-configurations'] as const,
    lists: () => [...queryKeys.reportConfigurations.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.reportConfigurations.lists(), filters] as const)
        : queryKeys.reportConfigurations.lists(),
    details: () => [...queryKeys.reportConfigurations.all(), 'detail'] as const,
    detail: (id: number) =>
      [...queryKeys.reportConfigurations.details(), { id }] as const,
    byProject: (projectId: ProjectId) =>
      [
        ...queryKeys.reportConfigurations.all(),
        'by-project',
        { projectId },
      ] as const,
  },

  // Report Instances
  reportInstances: {
    all: () => ['report-instances'] as const,
    lists: () => [...queryKeys.reportInstances.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.reportInstances.lists(), filters] as const)
        : queryKeys.reportInstances.lists(),
    details: () => [...queryKeys.reportInstances.all(), 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.reportInstances.details(), { id }] as const,
    byProject: (projectId: ProjectId) =>
      [
        ...queryKeys.reportInstances.all(),
        'by-project',
        { projectId },
      ] as const,
  },

  // Search
  search: {
    all: () => ['search'] as const,
    query: (q: string) => [...queryKeys.search.all(), { q }] as const,
  },

  // Firmware
  firmware: {
    all: () => ['firmware'] as const,
    lists: () => [...queryKeys.firmware.all(), 'list'] as const,
    list: () => queryKeys.firmware.lists(),
  },

  // Cloudloop Endpoints
  cloudloop: {
    all: () => ['cloudloop'] as const,
    endpoints: (loggerId: LoggerId) =>
      [...queryKeys.cloudloop.all(), 'endpoints', { loggerId }] as const,
    subscriber: (imei: string) =>
      [...queryKeys.cloudloop.all(), 'subscriber', { imei }] as const,
  },

  // Challenges
  challenges: {
    all: () => ['challenges'] as const,
    lists: () => [...queryKeys.challenges.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.challenges.lists(), filters] as const)
        : queryKeys.challenges.lists(),
  },

  // BOM Configuration
  bom: {
    all: () => ['bom'] as const,
    versions: () => [...queryKeys.bom.all(), 'versions'] as const,
    currentVersion: () => [...queryKeys.bom.all(), 'currentVersion'] as const,
    allParts: () => [...queryKeys.bom.all(), 'parts'] as const,
    parts: (versionId?: string, category?: string) =>
      [...queryKeys.bom.allParts(), { versionId, category }] as const,
    allRoutings: () => [...queryKeys.bom.all(), 'routings'] as const,
    routings: (versionId?: string) =>
      [...queryKeys.bom.allRoutings(), { versionId }] as const,
    allMappings: () => [...queryKeys.bom.all(), 'mappings'] as const,
    mappings: (versionId?: string) =>
      [...queryKeys.bom.allMappings(), { versionId }] as const,
    allConduitTransitions: () =>
      [...queryKeys.bom.all(), 'conduitTransitions'] as const,
    conduitTransitions: (versionId?: string) =>
      [...queryKeys.bom.allConduitTransitions(), { versionId }] as const,
  },

  // Product Services
  productServices: {
    all: () => ['productServices'] as const,
    lists: () => [...queryKeys.productServices.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.productServices.lists(), filters] as const)
        : queryKeys.productServices.lists(),
  },

  // Service Contracts
  serviceContracts: {
    all: () => ['serviceContracts'] as const,
    lists: () => [...queryKeys.serviceContracts.all(), 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.serviceContracts.lists(), filters] as const)
        : queryKeys.serviceContracts.lists(),
    details: () => [...queryKeys.serviceContracts.all(), 'detail'] as const,
    detail: (id: ServiceContractId) =>
      [...queryKeys.serviceContracts.details(), { id }] as const,
  },

  // Product Service Prices
  productServicePrices: {
    all: () => ['productServicePrices'] as const,
    current: (filters?: Record<string, unknown>) =>
      filters
        ? ([
            ...queryKeys.productServicePrices.all(),
            'current',
            filters,
          ] as const)
        : ([...queryKeys.productServicePrices.all(), 'current'] as const),
  },

  // Omnidot data
  omnidots: {
    all: () => ['omnidots'] as const,
    cycleHistogram: (siteId: number, filters?: Record<string, unknown>) =>
      filters
        ? ([
            ...queryKeys.omnidots.all(),
            'cycle-histogram',
            { siteId },
            filters,
          ] as const)
        : ([
            ...queryKeys.omnidots.all(),
            'cycle-histogram',
            { siteId },
          ] as const),
    dataFlags: (siteId: number) =>
      [...queryKeys.omnidots.all(), 'data-flags', { siteId }] as const,
  },

  // Reconciliation
  reconciliation: {
    all: () => ['reconciliation'] as const,
    discrepancies: () =>
      [...queryKeys.reconciliation.all(), 'discrepancies'] as const,
    serialCheck: (windowDays: number) =>
      [
        ...queryKeys.reconciliation.all(),
        'serial-check',
        { windowDays },
      ] as const,
  },

  // Current User
  self: {
    all: () => ['self'] as const,
  },

  // User Permissions (ad-hoc)
  perms: {
    all: () => ['perms'] as const,
    byUser: (userId: UserId) =>
      [...queryKeys.perms.all(), 'by-user', { userId }] as const,
  },

  // CMS Pages
  cmsPage: {
    all: () => ['cmsPage'] as const,
    types: () => [...queryKeys.cmsPage.all(), 'types'] as const,
    byType: (type: string) => [...queryKeys.cmsPage.all(), type] as const,
    detail: (type: string, id: string) =>
      [...queryKeys.cmsPage.byType(type), { id }] as const,
    history: (type: string, id: string) =>
      [...queryKeys.cmsPage.all(), 'history', type, id] as const,
  },

  // Landing Pages (CDN-first queries)
  landingPage: {
    all: () => ['landingPage'] as const,
    bySlug: (slug: string) =>
      [...queryKeys.landingPage.all(), 'slug', { slug }] as const,
    index: () => [...queryKeys.landingPage.all(), 'index'] as const,
  },

  // Blog (CDN-first queries)
  blogPage: {
    all: () => ['blogPage'] as const,
    bySlug: (slug: string) =>
      [...queryKeys.blogPage.all(), 'slug', { slug }] as const,
    index: () => [...queryKeys.blogPage.all(), 'index'] as const,
  },

  // Static Pages (CDN-first queries)
  staticPage: {
    all: () => ['staticPage'] as const,
    bySlug: (slug: string) =>
      [...queryKeys.staticPage.all(), 'slug', { slug }] as const,
    index: () => [...queryKeys.staticPage.all(), 'index'] as const,
  },

  // Contact Submissions
  contactSubmissions: {
    all: () => ['contactSubmissions'] as const,
    lists: () => [...queryKeys.contactSubmissions.all(), 'list'] as const,
    list: (params?: { page?: number; unreadOnly?: boolean }) =>
      params
        ? ([...queryKeys.contactSubmissions.lists(), params] as const)
        : queryKeys.contactSubmissions.lists(),
    details: () => [...queryKeys.contactSubmissions.all(), 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.contactSubmissions.details(), { id }] as const,
  },

  // TOS acceptances (admin report)
  tosAcceptances: {
    all: () => ['tosAcceptances'] as const,
    adminReport: () =>
      [...queryKeys.tosAcceptances.all(), 'adminReport'] as const,
  },

  // Forecasting interest requests (#3829): per-site button status + admin report
  forecastRequests: {
    all: () => ['forecastRequests'] as const,
    statusForSite: (siteId: SiteId) =>
      [...queryKeys.forecastRequests.all(), 'status', { siteId }] as const,
    // #3844: params in the key so each page caches independently.
    adminReport: (params: { limit: number; offset: number }) =>
      [...queryKeys.forecastRequests.all(), 'adminReport', params] as const,
  },

  // Paid per-site forecasting (#3982): the site page's offer and status
  forecastSubscription: {
    all: () => ['forecastSubscription'] as const,
    forSite: (siteId: SiteId) =>
      [...queryKeys.forecastSubscription.all(), { siteId }] as const,
  },

  // Per-site show_forecast admin control (#3834): list + toggle
  siteForecastVisibility: {
    all: () => ['siteForecastVisibility'] as const,
    adminReport: () =>
      [...queryKeys.siteForecastVisibility.all(), 'adminReport'] as const,
  },

  // Sea-ice forecast map page (#4586)
  seaIceForecast: {
    all: () => ['seaIceForecast'] as const,
    forSite: (siteId: SiteId) =>
      [...queryKeys.seaIceForecast.all(), 'site', { siteId }] as const,
  },

  // Webhook Events (admin / monitoring)
  webhookEvents: {
    all: () => ['webhookEvents'] as const,
    failed: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.webhookEvents.all(), 'failed', params] as const)
        : ([...queryKeys.webhookEvents.all(), 'failed'] as const),
    recent: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.webhookEvents.all(), 'recent', params] as const)
        : ([...queryKeys.webhookEvents.all(), 'recent'] as const),
    stats: (params?: { since?: string }) =>
      params
        ? ([...queryKeys.webhookEvents.all(), 'stats', params] as const)
        : ([...queryKeys.webhookEvents.all(), 'stats'] as const),
    details: () => [...queryKeys.webhookEvents.all(), 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.webhookEvents.details(), { id }] as const,
  },

  // Menu Projections (for select options)
  menuProjections: {
    all: () => ['menuProjection'] as const,
    cable: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.menuProjections.all(), 'cable', filters] as const)
        : ([...queryKeys.menuProjections.all(), 'cable'] as const),
    project: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.menuProjections.all(), 'project', filters] as const)
        : ([...queryKeys.menuProjections.all(), 'project'] as const),
    org: () => [...queryKeys.menuProjections.all(), 'org'] as const,
    site: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.menuProjections.all(), 'site', filters] as const)
        : ([...queryKeys.menuProjections.all(), 'site'] as const),
    user: () => [...queryKeys.menuProjections.all(), 'user'] as const,
    logger: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.menuProjections.all(), 'logger', filters] as const)
        : ([...queryKeys.menuProjections.all(), 'logger'] as const),
    order: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.menuProjections.all(), 'order', filters] as const)
        : ([...queryKeys.menuProjections.all(), 'order'] as const),
  },

  // User Landing (favorites + recents)
  userLanding: {
    all: () => ['userLanding'] as const,
    landing: () => [...queryKeys.userLanding.all(), 'landing'] as const,
    checkSiteFavorite: (siteId: SiteId) =>
      [
        ...queryKeys.userLanding.all(),
        'checkSiteFavorite',
        { siteId },
      ] as const,
    checkProjectFavorite: (projectId: ProjectId) =>
      [
        ...queryKeys.userLanding.all(),
        'checkProjectFavorite',
        { projectId },
      ] as const,
  },
} as const

/**
 * Mutation Key Factory
 *
 * Centralized mutation key management for React Query.
 *
 * Pattern: [action, resource, id?]
 *
 * The `delete.all()` key is used by DataTable's `useMutationState` to match
 * any pending delete mutation for row-level dimming. Individual resource keys
 * (e.g. `delete.cable(id)`) are used per-mutation for observability and deduplication.
 *
 * Device mutations use a separate `devices` namespace since they operate on
 * batches of device IDs rather than single resources.
 */
export const mutationKeys = {
  delete: {
    prefix: () => ['delete'] as const,
    cable: (id: CableId) => ['delete', 'cable', id] as const,
    challenge: (id: ChallengeId) => ['delete', 'challenge', id] as const,
    logger: (id: LoggerId) => ['delete', 'logger', id] as const,
    modem: (id: ModemId) => ['delete', 'modem', id] as const,
    note: (id: NoteId) => ['delete', 'note', id] as const,
    notification: (id: NotificationId) =>
      ['delete', 'notification', id] as const,
    org: (id: OrganizationId) => ['delete', 'org', id] as const,
    perm: (id: number | string) => ['delete', 'perm', id] as const,
    productService: (id: ProductServiceId) =>
      ['delete', 'productService', id] as const,
    project: (id: ProjectId) => ['delete', 'project', id] as const,
    serviceContract: (id: ServiceContractId) =>
      ['delete', 'serviceContract', id] as const,
    site: (id: number) => ['delete', 'site', id] as const,
    user: (id: UserId) => ['delete', 'user', id] as const,
    sensor: (id: SensorId) => ['delete', 'sensor', id] as const,
    reportConfiguration: (id: ReportConfigurationId) =>
      ['delete', 'reportConfiguration', id] as const,
    reportInstance: (id: ReportInstanceId) =>
      ['delete', 'reportInstance', id] as const,
    bomVersion: (id: string) => ['delete', 'bomVersion', id] as const,
    bomPart: (id: string) => ['delete', 'bomPart', id] as const,
    bomRouting: (id: string) => ['delete', 'bomRouting', id] as const,
    bomMapping: (id: string) => ['delete', 'bomMapping', id] as const,
    bomConduitTransition: (id: string) =>
      ['delete', 'bomConduitTransition', id] as const,
  },
  move: {
    prefix: () => ['move'] as const,
    cable: (id: CableId) => ['move', 'cable', id] as const,
    logger: (id: LoggerId) => ['move', 'logger', id] as const,
  },
  devices: {
    activate: (ids: number[]) => ['devices', 'activate', ids] as const,
    suspend: (ids: number[]) => ['devices', 'suspend', ids] as const,
  },
} as const

/**
 * Helper type to extract query key from factory
 *
 * @example
 * type OrderListKey = QueryKey<typeof queryKeys.orders.list>
 */
export type QueryKey<T extends (...args: any[]) => readonly any[]> =
  ReturnType<T>
